import React, { useState } from 'react';
import { Bell, Loader2, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * What a Slack notification would say (Task 3).
 *
 * WHY THIS SCREEN EXISTS.
 * The Slack app does not exist yet — Hayley has not created it — so nothing
 * Task 3 does can be delivered, seen or reviewed. Without this, the message
 * format would ship on the strength of unit tests and only be looked at after
 * the workspace was connected and people were already receiving DMs. Here it
 * can be read, corrected and approved first.
 *
 * It renders an approximation of Slack, not Slack. The exact typography and
 * spacing are Slack's to decide; what this shows faithfully is the content and
 * the order — which client, which task, what changed, what you can do about it.
 * The Block Kit JSON underneath is the real payload that would be sent.
 *
 * Nothing here sends anything. The endpoint runs the real recipient and
 * permission logic and stops short of delivering, which is why the recipient
 * table below shows genuine decisions rather than a guess.
 */

const TRIGGERS = [
  { value: 'task_assigned', label: 'Task assigned to someone' },
  { value: 'at_risk', label: 'Status changed to At Risk' },
  { value: 'collaborator_added', label: 'Collaborator added' },
  { value: 'mention', label: '@mentioned on a task' },
  { value: 'comment_added', label: 'Comment on a task someone is on' },
  { value: 'due_soon', label: 'Due date approaching' },
  { value: 'overdue', label: 'Task overdue' },
  { value: 'weekly_digest', label: 'Monday digest (your own week)' },
  { value: 'welcome', label: 'Welcome message (sent once, when somebody is switched on)' },
];

/**
 * The two that are not about a task, so the task number does nothing for them.
 * Disabling the input rather than ignoring it means an admin who types a number
 * and gets a message about their own week is not left wondering why.
 */
const TASKLESS_TRIGGERS = ['weekly_digest', 'welcome'];

/**
 * The plain-English version of each reason the dispatcher can give. The server
 * sends a stable code, not a sentence, so the wording lives here and only here.
 */
const REASONS: Record<string, string> = {
  self_acted: 'Made this change themselves',
  no_mapping: 'Not linked to a Slack account yet',
  not_permitted: 'Linked, but notifications are not switched on',
  muted: 'Has muted this kind of notification',
  duplicate: 'Already sent this in the last 24 hours',
  not_installed: 'No Slack workspace is connected yet',
};

interface Recipient {
  user_id: number;
  status: string;
  reason?: string;
  slack_user_id?: string;
}

interface SlackBlock {
  type: string;
  text?: { text: string };
  elements?: Array<{ text?: string | { text: string }; action_id?: string }>;
  fields?: Array<{ text: string }>;
}

interface Preview {
  task: { id: number; title: string; status: string; due_date: string | null } | null;
  trigger: string;
  project: { id: number; name: string } | null;
  message: { text: string; blocks: SlackBlock[] };
  recipients: Recipient[];
  // Welcome preview only: whose muted types the list in the message came from.
  basis?: 'own_settings' | 'tenant_defaults';
}

/**
 * Render Slack's mrkdwn the way Slack will.
 *
 * Without this the preview shows its own markup — "You have *1* task" with the
 * asterisks visible, and a multi-line section collapsed onto one line. That is
 * not a cosmetic problem here: the whole point of this screen is to show what
 * the message will look like, and a preview that lies about the formatting is
 * worse than no preview, because it invites a fix to something that was never
 * broken.
 *
 * Only the two things the builder actually emits are handled — *bold* and
 * _italic_ — rather than a general markdown parser. The entities are decoded
 * because the builder escapes &, < and > for Slack, which un-escapes them again
 * on the way out.
 */
const decodeEntities = (text: string): string =>
  text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');

const Mrkdwn: React.FC<{ text: string }> = ({ text }) => (
  <>
    {decodeEntities(text)
      .split('\n')
      .map((line, li) => (
        <React.Fragment key={li}>
          {li > 0 && <br />}
          {line.split(/(\*[^*]+\*|_[^_]+_)/g).map((part, pi) => {
            if (/^\*[^*]+\*$/.test(part)) {
              return <strong key={pi}>{part.slice(1, -1)}</strong>;
            }
            if (/^_[^_]+_$/.test(part)) {
              return <em key={pi} className="text-gray-500">{part.slice(1, -1)}</em>;
            }
            return <React.Fragment key={pi}>{part}</React.Fragment>;
          })}
        </React.Fragment>
      ))}
  </>
);

/**
 * Turn the Block Kit payload back into something readable.
 *
 * Deliberately a re-render of the blocks rather than a second copy of the
 * template: if the builder changes and this does not, the preview changes with
 * it. A preview maintained separately from the thing it previews stops being
 * true within a sprint.
 */
const SlackMessage: React.FC<{ blocks: SlackBlock[] }> = ({ blocks }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2.5">
    {blocks.map((block, i) => {
      if (block.type === 'context') {
        return (
          <p key={i} className="text-xs text-gray-500">
            <Mrkdwn text={block.elements?.map((e) => (typeof e.text === 'string' ? e.text : '')).join(' ') || ''} />
          </p>
        );
      }
      if (block.type === 'header') {
        return (
          <h4 key={i} className="text-base font-bold text-gray-900">
            {typeof block.text?.text === 'string' ? block.text.text : ''}
          </h4>
        );
      }
      if (block.type === 'section' && block.fields) {
        return (
          <div key={i} className="grid grid-cols-2 gap-x-6 gap-y-1.5 pt-1">
            {block.fields.map((f, j) => {
              const [label, ...rest] = f.text.split('\n');
              return (
                <div key={j} className="text-sm">
                  <span className="text-gray-500">{label.replace(/\*/g, '')}: </span>
                  <span className="text-gray-900">{decodeEntities(rest.join(' '))}</span>
                </div>
              );
            })}
          </div>
        );
      }
      if (block.type === 'section') {
        return (
          <p key={i} className="text-sm text-gray-800 leading-relaxed">
            <Mrkdwn text={block.text?.text || ''} />
          </p>
        );
      }
      if (block.type === 'divider') {
        return <hr key={i} className="border-gray-100" />;
      }
      if (block.type === 'actions') {
        return (
          <div key={i} className="flex flex-wrap gap-2 pt-1.5">
            {block.elements?.map((e, j) => (
              <span
                key={j}
                className={`px-3 py-1.5 text-xs font-medium rounded border ${
                  j === 0
                    ? 'bg-green-700 text-white border-green-700'
                    : 'bg-white text-gray-700 border-gray-300'
                }`}
              >
                {typeof e.text === 'object' ? e.text.text : e.text}
              </span>
            ))}
          </div>
        );
      }
      return null;
    })}
  </div>
);

const NotificationPreviewCard: React.FC = () => {
  const [taskId, setTaskId] = useState('');
  const [trigger, setTrigger] = useState(TRIGGERS[0].value);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [showJson, setShowJson] = useState(false);

  const taskless = TASKLESS_TRIGGERS.includes(trigger);

  const run = async () => {
    const trimmed = taskless ? '' : taskId.trim();
    if (trimmed && !/^\d+$/.test(trimmed)) {
      toast.error('A task number is digits only — or leave it blank.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/api/integrations/slack/notifications/preview', {
        params: trimmed ? { task_id: trimmed, trigger } : { trigger },
      });
      setPreview(res.data.preview);
    } catch (err: any) {
      setPreview(null);
      toast.error(err?.response?.data?.error || 'Could not build the preview.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-50 rounded-lg">
          <Bell className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Notification preview</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            See exactly what Slack would receive for a task. Nothing is sent. Leave the
            task number blank to use the most recently updated task.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Task number <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            value={taskless ? '' : taskId}
            disabled={taskless}
            onChange={(e) => setTaskId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && run()}
            placeholder={taskless ? 'Not used' : 'Most recent'}
            data-testid="slack-preview-task-id"
            className="w-36 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">What happened</label>
          <select
            value={trigger}
            onChange={(e) => setTrigger(e.target.value)}
            data-testid="slack-preview-trigger"
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {TRIGGERS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={run}
          disabled={loading}
          data-testid="slack-preview-run"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          Preview
        </button>
      </div>

      {preview && (
        <div className="space-y-4 pt-1" data-testid="slack-preview-result">
          <SlackMessage blocks={preview.message.blocks} />

          {preview.trigger === 'welcome' && (
            <p className="text-xs text-amber-700" data-testid="slack-preview-welcome-note">
              This goes out once, the first time somebody is switched on, and it cannot be
              sent again — so it is worth agreeing the wording before the rollout starts.
              {preview.basis === 'tenant_defaults'
                ? ' The list above is what a newly linked person would receive, from the defaults below.'
                : ' The list above reflects your own notification settings.'}
            </p>
          )}

          <div>
            <p className="text-xs font-medium text-gray-700 mb-1.5">Who would get this</p>
            {preview.recipients.length === 0 ? (
              <p className="text-xs text-gray-500">
                Nobody — this task has no owner or collaborators.
              </p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {preview.recipients.map((r) => (
                  <div key={r.user_id} className="flex items-center justify-between px-3 py-2 text-xs">
                    <span className="text-gray-700">User #{r.user_id}</span>
                    <span
                      className={
                        r.status === 'would_send' ? 'text-green-700 font-medium' : 'text-gray-500'
                      }
                    >
                      {r.status === 'would_send'
                        ? 'Would receive it'
                        : REASONS[r.reason || ''] || r.reason || 'Withheld'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <button
              onClick={() => setShowJson((v) => !v)}
              className="text-xs text-blue-600 hover:underline"
            >
              {showJson ? 'Hide' : 'Show'} the exact Slack payload
            </button>
            {showJson && (
              <pre className="mt-2 p-3 bg-gray-900 text-gray-100 text-[11px] rounded-lg overflow-x-auto">
                {JSON.stringify(preview.message, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default NotificationPreviewCard;
